import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import DashboardIcon from "../../assets/icons/DashboardIcon";
import ExerciseIcon from "../../assets/icons/ExerciseIcon";
import AthleteIcon from "../../assets/icons/AthleteIcon";
import {
  matchRoutes,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { objectToClassString } from "../../utils/functions";
import CustomButton from "../CustomButton/CustomButton";
import { RoutesReact } from "../../constants/routes";
import LeopartOnLightvector from "../../assets/icons/LeopartOnLightvector";
import "./SideBar.scss";
import { ThemeToggle } from "../ThemeToggle/ThemeToggle";
import { isDarkModeEnabled } from "../../providers/ThemeContext";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  FlagBannerIcon,
  CalendarDotsIcon,
  BellIcon,
  UsersThreeIcon,
  BrainIcon,
  BarbellIcon,
  ChartLineUpIcon,
  UploadSimpleIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { setSelectedEntity } from "../../store/reducers/me";
import { Avatar } from "@chakra-ui/react";
import Typhography from "../Typhography/Typhography";
import { AuthContext } from "../../providers/authProvider";
import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../../hooks/useEndpoints";
import { AlertsDropdown } from "../AlertsDropdown/AlertsDropdown";
import { useIndividualAthletes } from "../../pages/CarichiPage/hooks/useIndividualAthletes";
import { PlanQuotaWidget } from "./PlanQuotaWidget";
import { useModuleVisibility } from "../../hooks/useModuleVisibility";
interface ISideBar {}

interface ISidebarRoute {
  label: string;
  path: string;
  icon: JSX.Element;
}

/** Mappa path sidebar → moduleKey della config module-visibility
 *  (module-visibility/seed-default.json). Voci non mappate = sempre visibili. */
const SIDEBAR_MODULE_KEYS: Record<string, string> = {
  [RoutesReact.DIRECTOR_SPACES]: "teams-and-spaces",
  [RoutesReact.DIRECTOR_CALENDAR]: "calendar",
  [RoutesReact.DIRECTOR_PROGRAMMING]: "programming",
  [RoutesReact.DIRECTOR_CARICHI]: "carichi",
  [RoutesReact.DIRECTOR_USERS]: "users",
  [RoutesReact.DIRECTOR_SUBJECT_READINESS]: "readiness",
  [RoutesReact.DIRECTOR_NOTIFICATIONS]: "notifications",
  [RoutesReact.DIRECTOR_AI_COACH]: "ai-coach",
};

const SideBar: FC<ISideBar> = () => {
  //const { size } = useWindowDimensions();
  const navigate = useNavigate();
  const { isAdmin, isCoach, isDirector, isAthlete, userImage } =
    useContext(AuthContext);
  const selectedEntity = useAppSelector((st) => st.me.selectedEntity);
  const { getNotifications, getAlertsUnreadCount, getBodyMapAlertsUnreadCount } = useEndpoints();

  const notifQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data } = await getNotifications();
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const notifCount = useMemo(() => {
    const raw = notifQuery.data;
    const items = Array.isArray(raw) ? raw : ((raw as any)?.notifications ?? []);
    return items.length;
  }, [notifQuery.data]);

  const { data: alertsUnreadData } = useQuery({
    queryKey: ["alerts", "unread-count"],
    queryFn: () => getAlertsUnreadCount().then((r) => r.data),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
  const { data: bodyMapUnreadData } = useQuery({
    queryKey: ["body-map-alerts", "unread-count"],
    queryFn: () => getBodyMapAlertsUnreadCount().then((r) => r.data),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
  const alertsUnreadCount = (alertsUnreadData?.count ?? 0) + (bodyMapUnreadData?.count ?? 0);

  const { data: individualAthletes } = useIndividualAthletes();

  const [alertsOpen, setAlertsOpen] = useState(false);
  const alertsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!alertsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (alertsRef.current && !alertsRef.current.contains(e.target as Node)) {
        setAlertsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [alertsOpen]);

  const dispatch = useAppDispatch();
  const currentLocation = useLocation();
  const someRoutes = Object.values(RoutesReact).map((path) => ({ path }));
  const matches = matchRoutes(someRoutes, currentLocation);
  const type = useAppSelector((st) => st.me.me?.type);
  const entities = useAppSelector((st) => st.me.entities);

  const usersLength = useMemo(
    () =>
      [...(entities?.users || []), ...(entities?.directors || [])].length > 1,
    [entities]
  );

  // const isMobile = useMemo(() => size.width < 576, [size]);

  const { isVisible } = useModuleVisibility();

  const routes = useMemo<ISidebarRoute[]>(() => {
    const data: ISidebarRoute[] = [];
    if (isDirector || isAdmin) {
      data.push(
        {
          label: "Teams e spazi",
          path: RoutesReact.DIRECTOR_SPACES,
          icon: <FlagBannerIcon size={24} className="color" />,
        },

        {
          label: "Calendario",
          path: RoutesReact.DIRECTOR_CALENDAR,
          icon: <CalendarDotsIcon className="color__icon" />,
        },
        {
          label: "Programmazione",
          path: RoutesReact.DIRECTOR_PROGRAMMING,
          icon: <BarbellIcon className="color__icon" />,
        },
        {
          label: "Carichi",
          path: RoutesReact.DIRECTOR_CARICHI,
          icon: <ChartLineUpIcon className="color__icon" />,
        },
        {
          label: "Utenti",
          path: RoutesReact.DIRECTOR_USERS,
          icon: <UsersThreeIcon className="color__icon" />,
        },
        {
          label: "Readiness Soggettiva",
          path: RoutesReact.DIRECTOR_SUBJECT_READINESS,
          icon: <BrainIcon className="color__icon" />,
        },
        {
          label: "Notifiche",
          path: RoutesReact.DIRECTOR_NOTIFICATIONS,
          icon: <BellIcon className="color__icon" />,
        },
        {
          label: "AI Coach",
          path: RoutesReact.DIRECTOR_AI_COACH,
          icon: <SparkleIcon className="color__icon" weight="fill" />,
        }
      );
    } else if (isAthlete) {
      data.push({
        label: "Attività",
        path: RoutesReact.GYM_ACTIVITIES,
        icon: <ExerciseIcon className="color__icon" w={"24px"} h={"24px"} />,
      });
    } else if (isCoach) {
      data.push(
        {
          label: "Teams e spazi",
          path: RoutesReact.DIRECTOR_SPACES,
          icon: <FlagBannerIcon size={24} className="color" />,
        },

        {
          label: "Calendario",
          path: RoutesReact.DIRECTOR_CALENDAR,
          icon: <CalendarDotsIcon className="color__icon" />,
        },
        {
          label: "Programmazione",
          path: RoutesReact.DIRECTOR_PROGRAMMING,
          icon: <BarbellIcon className="color__icon" />,
        },
        {
          label: "Carichi",
          path: RoutesReact.DIRECTOR_CARICHI,
          icon: <ChartLineUpIcon className="color__icon" />,
        },
        {
          label: "Utenti",
          path: RoutesReact.DIRECTOR_USERS,
          icon: <UsersThreeIcon className="color__icon" />,
        },
        {
          label: "Notifiche",
          path: RoutesReact.DIRECTOR_NOTIFICATIONS,
          icon: <BellIcon className="color__icon" />,
        }
      );
    }

    // Feature-flag runtime: nasconde le voci dei moduli disabilitati dalla config.
    return data.filter((route) => {
      const moduleKey = SIDEBAR_MODULE_KEYS[route.path];
      return !moduleKey || isVisible(moduleKey);
    });
  }, [isAdmin, isAdmin, isAthlete, isCoach, isVisible]);

  const getActiveItemList = (path: string) => {
    if (!matches?.[0]) return false;

    if (path === RoutesReact.DIRECTOR_DASHBOARD) {
      return path === matches[0].pathname;
    }

    return matches[0].pathname.startsWith(path);
  };

  const renderRoutes = () => {
    return (
      <div className="container__routes">
        {routes.map(({ label, icon, path }) => (
          <div
            key={label}
            className={`container__item__list ${objectToClassString({
              container__item__list__active: getActiveItemList(path),
            })}`}
            onClick={() => navigate(path)}
          >
            {icon}
            <span className="text-medium-16">{label}</span>
            {path === RoutesReact.DIRECTOR_NOTIFICATIONS && notifCount > 0 && (
              <span className="notif__badge">{notifCount}</span>
            )}
          </div>
        ))}
      </div>
    );
  };
  // if (isMobile) return <MobileSideBar />;
  const renderRightUserSection = () => {
    switch (true) {
      case isAdmin:
        return (
          <div className="d-flex flex-column gap-1">
            <Typhography size="bold-12">
              {(selectedEntity as any).directorId.name}
            </Typhography>
            <Typhography size="medium-12">Amministratore</Typhography>
          </div>
        );
      case isCoach:
        return (
          <div className="d-flex flex-column gap-1">
            <Typhography size="bold-12">
              {(selectedEntity as any).directorId.name}
            </Typhography>
            <Typhography size="medium-12">Coach</Typhography>
          </div>
        );
      case isAthlete:
        return (
          <div className="d-flex flex-column gap-1">
            <Typhography size="bold-12">
              {(selectedEntity as any).directorId.name}
            </Typhography>
            <Typhography size="medium-12">Atleta</Typhography>
          </div>
        );
      case isDirector:
        return (
          <div className="d-flex flex-column gap-1">
            <Typhography size="bold-12">
              {(selectedEntity as any).name}
            </Typhography>
            <Typhography size="medium-12">Direttore</Typhography>
          </div>
        );
    }
  };
  return (
    <div className="d-flex h-100">
      <div className="container__sidebar">
        <p className="container__title">
          <LeopartOnLightvector w={"40px"} h={"40px"} />
          <span className="title">Leopart</span>
        </p>
        {renderRoutes()}

        {/* M6.2 · Atleti individuali del coach */}
        {(isDirector || isAdmin || isCoach) &&
          individualAthletes &&
          individualAthletes.length > 0 && (
          <div style={{ padding: '8px 0 4px 0' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#718096',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '4px 16px',
              }}
            >
              Atleti individuali
            </div>
            {individualAthletes.map((a) => (
              <div
                key={a._id}
                className="container__item__list"
                onClick={() =>
                  navigate(
                    RoutesReact.DIRECTOR_USER_ID.replace(':id', a._id),
                  )
                }
                style={{ cursor: 'pointer' }}
              >
                <span
                  style={{ fontSize: 14, color: 'inherit' }}
                >
                  {a.firstName} {a.lastName}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="container__support">
          <div ref={alertsRef} className="alerts__bell__container">
            <div
              className="alerts__bell__btn"
              onClick={() => setAlertsOpen((prev) => !prev)}
            >
              <BellIcon size={20} className="color__icon" />
              <span className="alerts__bell__label">Alert</span>
              {alertsUnreadCount > 0 && (
                <span className="notif__badge">
                  {alertsUnreadCount > 99 ? "99+" : alertsUnreadCount}
                </span>
              )}
            </div>
            {alertsOpen && (
              <div className="alerts__dropdown__wrapper">
                <AlertsDropdown onClose={() => setAlertsOpen(false)} />
              </div>
            )}
          </div>
          {/* Quick "+" globale Carichi (Addendum v8 EP8 menu globale top nav) */}
          <div
            className="container__item__list container__item__list__divider"
            onClick={() => navigate(RoutesReact.DIRECTOR_CARICHI)}
            title="Vai a Carichi → importa"
          >
            <UploadSimpleIcon className="color__icon" />
            <span className="text-medium-16">Carica dati sessione</span>
          </div>
          {/* Theme toggle (light/dark) — nascosto in produzione */}
          {isDarkModeEnabled && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "0 4px",
              }}
            >
              <ThemeToggle />
            </div>
          )}
          <div
            className="user__container"
            onClick={() => {
              navigate(RoutesReact.PROFILE);
            }}
          >
            <Avatar.Root className="avatar">
              <Avatar.Fallback className="fallback__container">
                <Typhography size="bold-14">{`${selectedEntity?.firstName[0]}${selectedEntity?.lastName[0]}`}</Typhography>
              </Avatar.Fallback>
              <Avatar.Image objectFit={"contain"} src={userImage} />
            </Avatar.Root>
            {renderRightUserSection()}
          </div>

          {isDirector && <PlanQuotaWidget />}

          {usersLength && (
            <CustomButton
              color="outline"
              fullWidth
              onClick={() => {
                dispatch(setSelectedEntity(undefined));
              }}
            >
              Cambia utenza
            </CustomButton>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  );
};

export default SideBar;
